import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore.ts";
import { login } from "../api/auth.ts";
import { api } from "../api/client.ts";
import { Loader2, UserPlus, ArrowLeft } from "lucide-react";
import DataNoticeModal from "../components/DataNoticeModal.tsx";

export const NOTICE_KEY = "neurox_data_notice_accepted";

type Mode = "login" | "register";

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [noticeAccepted, setNoticeAccepted] = useState(
    () => localStorage.getItem(NOTICE_KEY) === "true"
  );
  const navigate = useNavigate();

  function handleAcceptNotice() {
    localStorage.setItem(NOTICE_KEY, "true");
    setNoticeAccepted(true);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      {!noticeAccepted && <DataNoticeModal mode="accept" onAccept={handleAcceptNotice} />}

      <div className="w-full max-w-md p-8 border rounded-lg bg-card shadow-sm">
        {/* Logo */}
        <div className="mb-8">
          <img
            src="/neuroxrehab-logo.svg"
            alt="neuroXRehab"
            className="h-10 w-auto mb-2"
          />
          <p className="text-sm text-muted-foreground">Living Review Platform</p>
        </div>

        {mode === "login" ? (
          <LoginForm onRegisterClick={() => setMode("register")} onSuccess={() => navigate("/dashboard")} />
        ) : (
          <RegisterForm onBack={() => setMode("login")} />
        )}
      </div>
    </div>
  );
}

function LoginForm({
  onRegisterClick,
  onSuccess,
}: {
  onRegisterClick: () => void;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await login({ email, password });
      setUser(result.user as Parameters<typeof setUser>[0]);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            required
            autoComplete="email"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            required
            autoComplete="current-password"
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign in
        </button>
      </form>

      <div className="mt-6 pt-4 border-t">
        <p className="text-center text-xs text-muted-foreground mb-3">
          New to the platform? Request access below. You'll be assigned Observer status and a coordinator can promote your role.
        </p>
        <button
          onClick={onRegisterClick}
          className="w-full py-2 px-4 border rounded-md text-sm font-medium hover:bg-accent flex items-center justify-center gap-2"
        >
          <UserPlus className="h-4 w-4" />
          Request access
        </button>
      </div>
    </>
  );
}

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirm: string;
  affiliation: string;
  department: string;
  country: string;
  orcidId: string;
  website: string;
  bio: string;
}

const EMPTY_FORM: RegisterFormData = {
  name: "", email: "", password: "", confirm: "",
  affiliation: "", department: "", country: "",
  orcidId: "", website: "", bio: "",
};

function Field({
  id, label, type = "text", value, onChange, required, hint, autoComplete,
}: {
  id: string; label: string; type?: string; value: string;
  onChange: (v: string) => void; required?: boolean; hint?: string; autoComplete?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5" htmlFor={id}>
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
        {hint && <span className="text-muted-foreground font-normal ml-1 text-xs">{hint}</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        required={required}
        autoComplete={autoComplete}
      />
    </div>
  );
}

function RegisterForm({ onBack }: { onBack: () => void }) {
  const [form, setForm] = useState<RegisterFormData>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k: keyof RegisterFormData) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const { confirm: _c, ...payload } = form;
      // Strip empty optional strings so the backend doesn't store blanks
      const clean = Object.fromEntries(
        Object.entries(payload).filter(([, v]) => v !== "")
      );
      await api.post("/auth/self-register", clean);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="space-y-4 text-center">
        <div className="text-4xl">✅</div>
        <h2 className="font-semibold">Account created!</h2>
        <p className="text-sm text-muted-foreground">
          Your Observer account is ready. Sign in now. A coordinator or admin can promote your role.
        </p>
        <button
          onClick={onBack}
          className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
        >
          Go to sign in
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-5">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground" aria-label="Back">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="font-semibold text-sm">Request access</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* ── Account fields ── */}
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Account</p>
        <Field id="reg-name"     label="Full name"    value={form.name}     onChange={set("name")}     required autoComplete="name" />
        <Field id="reg-email"    label="Email"        type="email" value={form.email}    onChange={set("email")}    required autoComplete="email" />
        <Field id="reg-password" label="Password"     type="password" value={form.password} onChange={set("password")} required hint="min 8 chars" autoComplete="new-password" />
        <Field id="reg-confirm"  label="Confirm password" type="password" value={form.confirm}  onChange={set("confirm")}  required autoComplete="new-password" />

        {/* ── Researcher profile ── */}
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-2">Researcher profile</p>
        <Field id="reg-affiliation" label="Institution / affiliation" value={form.affiliation} onChange={set("affiliation")} autoComplete="organization" />
        <Field id="reg-department"  label="Department / research group" value={form.department}  onChange={set("department")} />
        <div className="grid grid-cols-2 gap-3">
          <Field id="reg-country" label="Country" value={form.country} onChange={set("country")} autoComplete="country-name" />
          <Field id="reg-orcid"   label="ORCID iD" value={form.orcidId} onChange={set("orcidId")} hint="0000-0000-…" />
        </div>
        <Field id="reg-website" label="Personal / lab website" type="url" value={form.website} onChange={set("website")} autoComplete="url" />
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="reg-bio">
            Brief bio <span className="text-muted-foreground font-normal text-xs">expertise / role in consortium</span>
          </label>
          <textarea
            id="reg-bio"
            value={form.bio}
            onChange={(e) => set("bio")(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 mt-1"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Create account
        </button>

        <p className="text-xs text-muted-foreground text-center">
          Accounts start as Observer. A coordinator or admin will promote your role.
        </p>
      </form>
    </>
  );
}
